import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import Maps from "../../components/Map";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import SearchAutocomplete from "../../components/SearchAutoComplete";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import areaService from "../../services/area";
import clusterService from "../../services/cluster";
import queryDefault from "../../utils/queryDefault";

export default function ClusterEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);

  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral>({
      lat: -6.1753917, // Default initial latitude
      lng: 106.8271533, // Default initial longitude
    });

  const schema = yup.object().shape({
    name: yup
      .string()
      .min(2)
      .required("name is a required field and length > 2"),
  });
  const form = useForm({
    defaultValues: {
      name: "",
      admin_name: user.username,
      area_id: "",
      location: { x: 0, y: 0 },
    },
    resolver: yupResolver(schema),
  });

  const handleMarkerDragEnd = (lat: number, lng: number) => {
    setMarkerPosition({ lat, lng });
    form.setValue("location.x", lat);
    form.setValue("location.y", lng);
  };

  const handlePlaceSelected = (
    place: google.maps.places.PlaceResult | null
  ) => {
    if (place) {
      setMarkerPosition({
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      });
      form.setValue("location", {
        x: place.geometry?.location?.lat() ?? 0,
        y: place.geometry?.location?.lng() ?? 0,
      });
    }
  };

  const updateCluster = useMutation(
    "update_cluster",
    clusterService.updateCluster,
    {
      onSuccess: () => {
        navigate("/app/properties/cluster");

        notification.onOpen({
          message: "Cluster was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const listArea = useQuery("list-area", () =>
    areaService.getAllArea({ keyword: "" })
  );

  const onSubmit = (values: any) => {
    const submittedValues = { ...values, id };
    updateCluster.mutate(submittedValues);
  };

  const currentCluster = useQuery("current-cluster", () =>
    clusterService.getClusterById(Number(id))
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    if (currentCluster.isFetched && currentCluster.data && !isInitialized.current) {
      const clusterData = currentCluster.data.data;
      form.setValue("name", clusterData.name);
      form.setValue("area_id", clusterData.area_id);
      form.setValue("location", {
        x: clusterData.location.x,
        y: clusterData.location.y,
      });
      setMarkerPosition({
        lat: clusterData.location.x,
        lng: clusterData.location.y,
      });
      isInitialized.current = true;
    }
  }, [currentCluster.isFetched, currentCluster.data, form]);

  return (
    <MainLayout>
      <Breadcrumb
        label={`Edit Cluster ${get(currentCluster, "data.data.name")}`}
        breadcrumbs={[
          { label: "Clusters", href: "/app/properties/cluster" },
          {
            label: "Edit Cluster",
            href: `/app/properties/cluster/edit/${id}`,
          },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="cluster"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Area</FormLabel>
                <Controller
                  name="area_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {get(listArea, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <SearchAutocomplete onPlaceSelected={handlePlaceSelected} />
              </FormControl>
              <Box
                sx={{
                  width: "100%",
                  height: "300px",
                }}
              >
                <Maps
                  center={markerPosition}
                  onMarkerDragEnd={handleMarkerDragEnd}
                  dragable={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateCluster.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
